import { Expose } from 'class-transformer';
import { PccAuditInfo } from './audit-info.typings';

export type PccSupplementOrderStatus = 'active' | 'pending confirmation' | 'on hold';

export class PccSupplementOrderParams {
	statuses?: PccSupplementOrderStatus[];
}

/**
 * Represents an administrative order for supplement orders
 */
export class PccAdministrativeOrder {
	/**
	 * Administrative Order ID
	 */
	adminOrderId: number;

	/**
	 * The person who confirmed the Administrative Order
	 */
	confirmedBy: string;

	/**
	 * The person who created the Administrative Order
	 */
	createdBy: string;

	/**
	 * Effective Date of the Administrative Order
	 */
	effectiveDate: string;

	/**
	 * In Effective Date of the Administrative Order
	 */
	inEffectiveDate: string;

	/**
	 * 'Y' if the Administrative Order been verified
	 */
	orderVerified: string;

	/**
	 * The person who ordered the Administrative Order
	 */
	orderedBy: string;

	/**
	 * Physician Order ID
	 */
	physOrderId: number;

	/**
	 * The type of Administrative Order. Ie. Hold, Resume, etc.
	 */
	relationship: string;

	/**
	 * Represents the details of the administrative order.
	 *
	 * SimpliScreen maps the administrative order to the following string for embedding in the RAG:
	 * relationship: {relationship}, effectiveDate: {effectiveDate}, inEffectiveDate: {inEffectiveDate}, orderVerified: {orderVerified}, orderedBy: {orderedBy}, confirmedBy: {confirmedBy}
	 */
	details: string;

	constructor(partial?: Partial<PccAdministrativeOrder>) {
		Object.assign(this, partial);
	}

	@Expose({ name: 'details' })
	getDetails() {
		const details = [];
		if (this.relationship) details.push(`Relationship: ${this.relationship}`);
		if (this.effectiveDate) details.push(`Effective Date: ${this.effectiveDate}`);
		if (this.inEffectiveDate) details.push(`In Effective Date: ${this.inEffectiveDate}`);
		if (this.orderVerified) details.push(`Order Verified: ${this.orderVerified}`);
		if (this.orderedBy) details.push(`Ordered By: ${this.orderedBy}`);
		if (this.confirmedBy) details.push(`Confirmed By: ${this.confirmedBy}`);
		return details.join(', ');
	}
}

/**
 * Represents a supplement order schedule
 */
export class PccSupplementOrderSchedule {
	/**
	 * Free text directions entered for the supplement order. Directions can be added at the time of entering an order or as an update after the order has been promoted to the patient's chart.
	 */
	additionalDirections?: string;

	/**
	 * The end date and time of schedule. Date and time are expressed in UTC (Coordinated Universal Time), with a special UTC designator ("Z").
	 */
	endDateTime?: string;

	/**
	 * Directions for the supplement order task
	 */
	scheduleDirections: string;

	/**
	 * The start date and time of schedule. Date and time are expressed in UTC (Coordinated Universal Time), with a special UTC designator ("Z").
	 */
	startDateTime: string;

	/**
	 * Represents type of schedule. Possible values are Routine, PRN, OneTime or STAT.
	 */
	type: 'Routine' | 'PRN' | 'OneTime' | 'STAT';

	/**
	 * Represents the details of the supplement order schedule.
	 *
	 * SimpliScreen maps the supplement order schedule to the following string for embedding in the RAG:
	 * scheduleType: {type}, scheduleDirections: {scheduleDirections}, additionalDirections: {additionalDirections}, startDateTime: {startDateTime}, endDateTime: {endDateTime}
	 */
	details: string;

	constructor(partial?: Partial<PccSupplementOrderSchedule>) {
		Object.assign(this, partial);
	}

	@Expose({ name: 'details' })
	getDetails() {
		const details = [];
		if (this.type) details.push(`Schedule Type: ${this.type}`);
		if (this.scheduleDirections) details.push(`Schedule Directions: ${this.scheduleDirections}`);
		if (this.additionalDirections) details.push(`Additional Directions: ${this.additionalDirections}`);
		if (this.startDateTime) details.push(`Start DateTime: ${this.startDateTime}`);
		if (this.endDateTime) details.push(`End DateTime: ${this.endDateTime}`);
		return details.join(', ');
	}
}

/**
 * Represents a supplement order for a patient in PointClickCare
 *
 * SimpliScreen maps the supplement order to the following string for embedding in the RAG:
 * description: {description}, status: {status}, orderedBy: {orderedBy}, orderDateTime: {orderDateTime}
 * \t Schedules: scheduleType: {type}, scheduleDirections: {scheduleDirections}, additionalDirections: {additionalDirections}, startDateTime: {startDateTime}, endDateTime: {endDateTime}
 * \t Administrative Orders: relationship: {relationship}, effectiveDate: {effectiveDate}, inEffectiveDate: {inEffectiveDate}, orderVerified: {orderVerified}, orderedBy: {orderedBy}, confirmedBy: {confirmedBy}
 *
 * @see https://connect2.pointclickcare.com/api/public/preview1/orgs/{orgUuid}/supplement-orders?patientId={patientId}
 */
export class PccSupplementOrder extends PccAuditInfo {
	/**
	 * List of administrative orders for this supplement order
	 */
	administrativeOrders: PccAdministrativeOrder[];

	/**
	 * Description of the order. Represents Diet Supplement
	 */
	description: string;

	/**
	 * Represents the date/time when the supplement order was ordered. The date and time should be expressed in UTC in the following format: YYYY-MM-DDTHH:MM:SS.sssZ.
	 */
	orderDateTime: string;

	/**
	 * Represents the PointClickCare user who ordered the supplement order. This is typically the practitioner or medical professional assigned to the patient.
	 */
	orderedBy: string;

	/**
	 * Unique identifier for the practitioner or medical professional that ordered the supplement order
	 */
	orderedById: number;

	/**
	 * PointClickCare's unique identifier for a patient within the organization.
	 *
	 * When a patient is admitted to a facility within an organization, they are assigned a patientId which is unique to their stay within that facility. If the same patient were to get admitted to another facility within the same organization in the future, they would be assigned a new patientId. The patientId however is unique at the organization level. That is, no two patients across different facilities within the organization will have the same patientId.
	 *
	 * This identifier is crucial for tracking patient information and care but must not be displayed in any user interface due to legal privacy requirements.
	 */
	patientId: number;

	/**
	 * List of schedules for this supplement order
	 */
	schedules: PccSupplementOrderSchedule[];

	/**
	 * Represents the status of the supplement order for the patient. Possible values are "active", "pending confirmation", and "on hold".
	 */
	status: 'active' | 'pending confirmation' | 'on hold';

	/**
	 * Represents the ID of the diet supplement on the order for the patient
	 */
	supplementId: number;

	/**
	 * PointClickCare's unique identifier for a Supplement Order
	 */
	supplementOrderId: number;

	/**
	 * Represents the details of the supplement order.
	 *
	 * SimpliScreen maps the supplement order to the following string for embedding in the RAG:
	 * description: {description}, status: {status}, orderedBy: {orderedBy}, orderDateTime: {orderDateTime}
	 * \t Schedules: scheduleType: {type}, scheduleDirections: {scheduleDirections}, additionalDirections: {additionalDirections}, startDateTime: {startDateTime}, endDateTime: {endDateTime}
	 * \t Administrative Orders: relationship: {relationship}, effectiveDate: {effectiveDate}, inEffectiveDate: {inEffectiveDate}, orderVerified: {orderVerified}, orderedBy: {orderedBy}, confirmedBy: {confirmedBy}
	 */
	details: string;

	constructor(partial?: Partial<PccSupplementOrder>) {
		super();
		Object.assign(this, partial);
		if (this.schedules) this.schedules = this.schedules.map((schedule) => new PccSupplementOrderSchedule(schedule));
		if (this.administrativeOrders) this.administrativeOrders = this.administrativeOrders.map((order) => new PccAdministrativeOrder(order));
	}

	@Expose({ name: 'details' })
	getDetails(skipDescription = false) {
		const details = [`Data Type: Supplement Order`];
		if (this.supplementOrderId) details.push(`Supplement Order ID: ${this.supplementOrderId}`);
		if (this.description && !skipDescription) details.push(`Description: ${this.description}`);
		if (this.status) details.push(`Status: ${this.status}`);
		if (this.orderedBy) details.push(`Ordered By: ${this.orderedBy}`);
		if (this.orderDateTime) details.push(`Order DateTime: ${this.orderDateTime}`);
		return `${details.join(', ')}\n${(this.schedules || []).map((schedule) => `\t${schedule.getDetails()}`).join('\n')}\n${(this.administrativeOrders || []).map((order) => `\t${order.getDetails()}`).join('\n')}`;
	}
}
